import { createSelector } from '@app/utils/createSelector'

import type { V3CalendarFeed } from '@app/packages/api/v3/types/lib'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callApi } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

export type CalendarFeedLinks = V3CalendarFeed['attributes']

const calendarFeedSlice = createReduxSlice<SliceState<CalendarFeedLinks | null>>('calendar_feed')

/** The sitter's link, or `null` before they create one; it changes only through create and reset, so it loads once. */
export const calendarFeedResultSelector = createSelector([calendarFeedSlice.selector], sliceState =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: calendarFeedSlice,
      load: async dispatch => {
        const { getCalendarFeed } = await import('@app/packages/api/v3/actions/calendar_feed')
        return (await dispatch(callApi(getCalendarFeed()))).data?.attributes ?? null
      },
    })
  )
)

/** Creates the link, or hands back the one already there. */
export const createCalendarFeed = () =>
  createThunk(async dispatch => {
    const { postCalendarFeed } = await import('@app/packages/api/v3/actions/calendar_feed')
    const resp = await dispatch(callApi(postCalendarFeed()))
    dispatch(calendarFeedSlice.set({ hash: '', expires_at: '', data: resp.data.attributes }))
    return resp.data.attributes
  })

/** Issues a new link; the old one stops answering at once. */
export const resetCalendarFeed = () =>
  createThunk(async dispatch => {
    const { postCalendarFeedReset } = await import('@app/packages/api/v3/actions/calendar_feed')
    const resp = await dispatch(callApi(postCalendarFeedReset()))
    dispatch(calendarFeedSlice.set({ hash: '', expires_at: '', data: resp.data.attributes }))
  })
